import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { API_ENDPOINTS } from '../config/api'
import { Button, Input, Alert, Card } from '../components/ui'

const ForgotPasswordPage: React.FC = () => {
  const intl = useIntl()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    try {
      const response = await fetch(API_ENDPOINTS.auth.forgotPassword, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      if (!response.ok) throw new Error('Failed to send reset email')
      setSent(true)
    } catch {
      setError(intl.formatMessage({ id: 'auth.forgot.genericError' }))
    } finally {
      setIsLoading(false)
    }
  }

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4">
        <div className="max-w-md w-full text-center">
          <Card padding="lg">
            <div className="text-4xl mb-4">📧</div>
            <h2 className="text-xl font-bold text-foreground mb-2"><FormattedMessage id="auth.forgot.checkEmailTitle" /></h2>
            <p className="text-sm text-foreground-muted mb-6">
              <FormattedMessage id="auth.forgot.checkEmailBody" values={{ email }} />
            </p>
            <Link to="/auth/signin" className="text-sm text-primary hover:text-primary font-medium">
              <FormattedMessage id="auth.forgot.backToSignIn" />
            </Link>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4">
      <div className="max-w-md w-full space-y-6">
        <div>
          <h2 className="text-center text-3xl font-extrabold text-foreground"><FormattedMessage id="auth.forgot.title" /></h2>
          <p className="mt-2 text-center text-sm text-foreground-muted">
            <FormattedMessage id="auth.forgot.subtitle" />
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="error">{error}</Alert>
          )}

          <Input
            label={intl.formatMessage({ id: 'auth.emailAddress' })}
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder={intl.formatMessage({ id: 'auth.emailPlaceholder' })}
          />

          <Button type="submit" fullWidth loading={isLoading}>
            {isLoading
              ? intl.formatMessage({ id: 'auth.sending' })
              : intl.formatMessage({ id: 'auth.forgot.send' })}
          </Button>
        </form>

        <div className="text-center">
          <Link to="/auth/signin" className="text-sm text-primary hover:text-primary font-medium">
            <FormattedMessage id="auth.forgot.backToSignIn" />
          </Link>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage
